import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

import { useAppSelector } from 'store/store';
import { deliveryZipSelector } from 'store/selectors/locationServicesSelector';
import {
  getFeatureFlagByNameSelector,
  storeIdentifierNameSelector,
} from 'store/selectors/commonSelector';
import FavoritesEcode from 'common/Favorites/FavoritesEcode';
import useLogger from 'hooks/useLogger';
import { AlertBanner, Button } from '@dsg-tech/homefield-react';
import { HomefieldIconSignCircledCheck } from '@dsg-tech/homefield-react-icons';
import { checkComingSoonEndDate, checkIfPreOrderSKU } from '../../utility/utils';
import { quickviewProductAddToCartEvent } from '../../analytics/analytics-events';
import { envData } from '../../utility/environments';
import AddToFavorites from './AddToFavorites';
import { QuickviewDetails, SkuDetails } from '../FulfillmentOptions/types';

type AddToCartProps = {
  onClose: () => void;
  count: number;
  selectedSKU: SkuDetails | null;
  setValidate: (validate: boolean) => false;
  selectedStoreId: string;
  selectedMethod: string;
  quickviewDetails: QuickviewDetails;
  callback: () => void;
  attributesState: { [key: string]: string };
  updateSignal: () => void;
};

type ButtonMessageProps = {
  showSuccessMessage: boolean;
  label: string;
  handleOnClick: () => void;
  isButtonDisabled: boolean;
  size: 'medium' | 'large' | 'small' | undefined;
};

const ButtonMessage = ({
  showSuccessMessage,
  label,
  handleOnClick,
  isButtonDisabled,
  size,
}: ButtonMessageProps) =>
  showSuccessMessage ? (
    <Button
      variant="marketing"
      size={size}
      className="button-container hmf-py-xs"
      text={label}
      label={label}
      icon={<HomefieldIconSignCircledCheck />}
    />
  ) : (
    <Button
      variant="primary"
      size={size}
      className="button-container hmf-py-xs"
      text={label}
      onClick={handleOnClick}
      disabled={isButtonDisabled}
    />
  );

const AddToCart = (props: AddToCartProps) => {
  const {
    onClose,
    count,
    selectedSKU,
    setValidate,
    selectedStoreId,
    selectedMethod,
    quickviewDetails,
    callback,
    attributesState,
    updateSignal,
  } = props;
  const { warn, info } = useLogger();

  const [label, setLabel] = useState(
    quickviewDetails.style.buyable === 1 ? 'Add To Cart' : 'IN-STORE ONLY',
  );
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [afterCartAdd, setAfterCartAdd] = useState(false);
  const [lockAddToCart, setLockAddToCart] = useState(false);
  const [error, setError] = useState(false);
  const [successMsgTimeout, setSuccessMsgTimeout] = useState<NodeJS.Timeout | null>(null);
  const deliveryZip = useAppSelector(deliveryZipSelector);
  const favoriteIconEnabled = useAppSelector(getFeatureFlagByNameSelector('addFavoritesEnabled'));
  const storeIdentifierName = useAppSelector(storeIdentifierNameSelector);
  const skuFavoritesEnabled = useAppSelector(getFeatureFlagByNameSelector('skuFavoritesEnabled'));

  useEffect(() => {
    setAfterCartAdd(false);
    setLockAddToCart(false);

    if (quickviewDetails.style.buyable === 0) {
      setLabel('IN-STORE ONLY');
    } else if (selectedSKU) {
      if (selectedSKU.comingSoonEndDate && checkComingSoonEndDate(selectedSKU.comingSoonEndDate)) {
        setLabel('COMING SOON');
      } else if (checkIfPreOrderSKU(selectedSKU)) {
        setLabel('PRE-ORDER');
      } else {
        setLabel('Add To Cart');
      }
    } else {
      setLabel('Add To Cart');
    }
  }, [selectedSKU, count]);

  useEffect(() => {
    return () => {
      if (successMsgTimeout) {
        clearTimeout(successMsgTimeout);
      }
    };
  }, [successMsgTimeout]);

  const cartAddRequestParams = () => {
    let params = `?qty=${count || 1}`;

    if (selectedMethod && selectedMethod === 'BOPIS') {
      params += `&bopis=true&store=${selectedStoreId}`;
    } else if (selectedMethod && selectedMethod === 'BOPL') {
      params += `&bopl=true&store=${selectedStoreId}`;
    } else if (selectedMethod && selectedMethod === 'SDD') {
      params += `&sdd=true&zipcode=${deliveryZip}`;
    }

    return params;
  };

  const handleAddToCartAnalyticsEvent = () => {
    try {
      quickviewProductAddToCartEvent({
        AvailableQTY: selectedMethod === 'SHIP' ? selectedSKU?.shipQty : selectedSKU?.bopisQty,
        CartAdditionSource: 'QuickView',
        CurrentPricePerUnit: selectedSKU?.prices.offerPrice,
        FulfillmentMethod:
          selectedMethod === 'SHIP' ? 'Ship To Customer' : 'In Store Pickup (BOPIS)',
        ...(selectedMethod === 'BOPIS' ? { FulfillmentStore: selectedStoreId } : {}),
        ...(selectedSKU?.prices?.priceIndicator === 0
          ? { ListPricePerUnit: selectedSKU?.prices?.listPrice }
          : {}),
        LowStockMessageShown: !!(
          quickviewDetails.style.lowStock && quickviewDetails.style.lowStock === true
        ),
        ProductID: selectedSKU?.parentPartNumber,
        ProductName: selectedSKU?.name,
        ProductQty: count,
        ProductSKU: selectedSKU?.partNumber,
      });
    } catch (e) {
      info('Quickview AddToCart Analytics Failed: ', e);
    }
  };

  const handleCartError = () => {
    setError(true);
    setLabel(`Add To Cart`);
    setLockAddToCart(false);
  };

  const handleCartAddSuccess = () => {
    setShowSuccessMessage(true);
    setLabel('Item Added To Cart!');

    // when called, directly updates the React state of the cart count
    if (window.cartCountComponent?.setCartCount) {
      const currentCartQty = parseInt(document.getElementById('OrderQty')?.innerText ?? '0', 10);
      window.cartCountComponent.setCartCount(currentCartQty + count);
    }
  };

  const handleAfterCartAdd = () => {
    setShowSuccessMessage(false);
    setAfterCartAdd(true);
    setLabel('View Cart');
  };

  const handleCartAdd = async () => {
    setLockAddToCart(true);
    try {
      // Determine if localhost and point the host to the locally running cart engine
      let hostDomain = '';
      if (window.location.href.indexOf('localhost') > -1) {
        hostDomain = 'http://localhost:8070';
      }

      // Create PUT url to new cart api
      const newCartPutRequest = `${hostDomain}/api/v1/carts/contents/${selectedSKU?.partNumber}`;

      const queryParams = cartAddRequestParams();

      // PUT to the new cart api with query params
      const response = await axios.put(newCartPutRequest + queryParams, null, {
        withCredentials: true,
      });

      callback();

      handleAddToCartAnalyticsEvent();

      if (response.status === 200) {
        // Cart add successful show success message for 3 seconds
        handleCartAddSuccess();
        const successMsgDelay = setTimeout(() => {
          // After 3 seconds remove state state and change button label to 'View Cart'
          handleAfterCartAdd();
        }, 3000);
        setSuccessMsgTimeout(successMsgDelay);
        return true;
      }
      return false;
    } catch (e) {
      warn('AddToCart - ', e);
      handleCartError();
      return false;
    }
  };

  const handleAddToCart = () => {
    try {
      setValidate(true);
      if (selectedSKU && selectedMethod) {
        handleCartAdd();
      }
    } catch (e) {
      info('handleAddToCart - ', e);
      handleCartError();
    }
  };

  const isButtonDisabled = useMemo(() => {
    return (
      (quickviewDetails.style.definingAttributes?.length === 0 &&
        quickviewDetails.skus.length > 1) ||
      quickviewDetails.style.buyable === 0 ||
      !selectedSKU ||
      !selectedMethod ||
      (selectedSKU.comingSoonEndDate && checkComingSoonEndDate(selectedSKU.comingSoonEndDate))
    );
  }, [quickviewDetails, selectedSKU, selectedMethod]);

  const handleViewCart = () => {
    if (window?.location?.pathname !== '/OrderItemDisplay') {
      window.location.href = `/OrderItemDisplay?calculationUsageId=-1&calculationUsageId=-2&calculationUsageId=-3&calculationUsageId=-7&updatePrices=1&doConfigurationValidation=Y&catalogId=${envData[storeIdentifierName].catalogId}&orderId=.&langId=-1&storeId=${envData[storeIdentifierName].storeId}`;
      return;
    }

    onClose();
  };

  const handleOnClick = () => (lockAddToCart ? () => {} : handleAddToCart());

  return (
    <div
      className="hmf-display-flex hmf-flex-column hmf-mt-xs"
      data-testid="quickViewModal_actionButton">
      {error && (
        <AlertBanner
          type="alert"
          title="Cart Error"
          message="We are sorry! Please refresh the page and add to cart again. Need help?"
          linkTitle="Contact Us"
          link="/s/letschat"
          customClass="hmf-mb-s"
        />
      )}
      <div
        className={`hmf-display-flex hmf-flex-row ${
          afterCartAdd && 'hmf-flex-md-nowrap hmf-flex-wrap'
        }`}>
        {favoriteIconEnabled &&
          (skuFavoritesEnabled ? (
            <AddToFavorites
              template="small"
              onClose={onClose}
              quickviewDetails={quickviewDetails}
              callback={callback}
              attributesState={attributesState}
              selectedSKU={selectedSKU}
              updateSignal={updateSignal}
            />
          ) : (
            <FavoritesEcode
              ecode={quickviewDetails.style.partNumber}
              storeIdentifierName={storeIdentifierName}
            />
          ))}
        {afterCartAdd && (
          <Button
            variant="tertiary"
            size="large"
            className="quickview-keepShopping hmf-mr-0 hmf-mr-md-xs button-container hmf-py-xs"
            text="Keep Shopping"
            onClick={onClose}
          />
        )}
        {afterCartAdd ? (
          <Button
            variant="primary"
            size="large"
            className="quickview-viewCart hmf-mt-xs hmf-mt-md-0 button-container hmf-py-xs"
            text={label}
            label={label}
            onClick={handleViewCart}
          />
        ) : (
          <ButtonMessage
            showSuccessMessage={showSuccessMessage}
            label={label}
            handleOnClick={handleOnClick}
            isButtonDisabled={isButtonDisabled}
            size="large"
          />
        )}
      </div>
    </div>
  );
};

export default AddToCart;
